import {
  Box,
  Button,
  Chip,
  Divider,
  EllipsisVIcon,
  GreyIconWrapper,
  Link,
  PencilAltIcon,
  SingleInfoCard,
  Theme,
  TrashIcon,
  Typography,
  User,
  createStyles,
  makeStyles,
  useModal,
} from '@forgeglobal/design-framework'
import { CommunicationSource } from './ConversationsSidebarPanel'
import { ConversationModal } from './ConversationModal'
import { DeleteConversationModal } from './DeleteConversationModal'
import {
  InteractionDataFragment,
  InteractionFilter,
  InteractionWithRollupsDataFragment,
} from 'graphql/bindings/AdminBindings'
import {
  PopoverAction,
  PopoverListItem,
  PopoverListItemIcon,
  PopoverListItemText,
  StyleDefaults,
} from 'isolated-design-library'
import { getInitials } from 'util/AccountUtils'
import { getInteractionIcon } from 'util/InteractionUtils'
import CommunicationInput from 'components/communication-input/CommunicationInput'
import React, { Fragment } from 'react'
import moment from 'moment'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    defaultButton: {
      height: theme.spacing(4),
      textTransform: 'none',
      color: StyleDefaults.palette.blue.dark,
      backgroundColor: StyleDefaults.palette.surface.main,
      borderColor: StyleDefaults.palette.surface.dark,
      borderLeftColor: `${StyleDefaults.palette.surface.dark} !important`,
      flexGrow: 1,
    },
    selectedButton: {
      backgroundColor: `${theme.palette.common.white} !important`,
      color: `${theme.palette.secondary.main} !important`,
      borderColor: StyleDefaults.palette.surface.dark,
    },
    buttonGroup: {
      borderRadius: theme.spacing(0.5),
      width: '100%',
    },
    boxClass: {
      display: 'table',
      '& > *': {
        display: 'table-row',
      },
      '& > * > *': {
        display: 'table-cell',
        '&:first-child': {
          whiteSpace: 'nowrap',
        },
        '&:last-child': {
          width: '100%',
        },
      },
    },
  })
)

export const ConversationInfoCard: React.FC<{
  interaction: InteractionWithRollupsDataFragment
  currentId: string
  isAccount?: boolean
  isPreview?: boolean
  accountName?: string
  groupFilter: InteractionFilter
}> = ({ interaction, currentId, isAccount, accountName, groupFilter, isPreview }) => {
  const classes = useStyles()
  const [showModal, hideModal] = useModal(() => {
    return (
      <ConversationModal
        hideModal={hideModal}
        formType="edit"
        objId={currentId}
        isAccount={isAccount}
        accountName={accountName}
        conversation={interaction}
      />
    )
  }, [interaction, accountName, currentId, isAccount])

  const sourceId = (interaction: InteractionDataFragment) => {
    const source = interaction.source
    if (!source) return null
    switch (source.__typename) {
      case 'AccountSource':
        return source.account.id
      case 'OpportunitySource':
        return source.opportunity.id
    }
  }

  const [showDeleteModal, hideDeleteModal] = useModal(() => {
    const interactionId = interaction.id
    return (
      <DeleteConversationModal
        hideModal={hideDeleteModal}
        conversationId={interactionId}
        accountId={isAccount ? currentId : undefined}
        opportunityId={isAccount ? undefined : currentId}
        groupFilter={groupFilter}>
        <ConversationInfoCard
          interaction={interaction}
          currentId={currentId}
          isAccount={isAccount}
          accountName={accountName}
          isPreview={true}
          groupFilter={groupFilter}
        />
      </DeleteConversationModal>
    )
  }, [currentId, isAccount, groupFilter, interaction, accountName])

  const frontMessageGroup = interaction?.front_message_group
  const frontSender = (frontMessageGroup?.message_contacts ?? []).find(contact => contact.role === 'from')
  let frontSenderIdentifier: string | undefined
  if (frontSender) {
    if (frontSender.account) {
      frontSenderIdentifier = frontSender.account.name
    } else {
      frontSenderIdentifier = frontSender.email
    }
  }
  const value = JSON.parse(interaction.tagged_value)
  const recordedBy = interaction.recorder
    ? `${interaction.recorder.first_name} ${interaction.recorder.last_name}`
    : frontSenderIdentifier || 'System'
  const createdAtMoment = moment(interaction.created_at)
  const isSameDay = createdAtMoment.isSame(moment(), 'day')
  const createdAt = isSameDay ? createdAtMoment.format('[Today] h:mm a') : createdAtMoment.format('M/D/YY h:mm a')
  const isSubjectOfInteraction = sourceId(interaction) === currentId
  let from = null
  let to = null
  let additionalToCount = 0
  let subject = ''

  if (frontMessageGroup) {
    const toContacts = (frontMessageGroup?.message_contacts ?? []).filter(
      contact => contact.role === 'to' || contact.role === 'cc'
    )
    if (frontSender) {
      if (frontSender.account) {
        from = <Chip avatar={<User>{getInitials(frontSender.account.name)}</User>} label={frontSender.account.name} />
      } else {
        from = <Chip label={frontSender.email} />
      }
    }
    const toContactList = toContacts.map(contact => {
      if (contact) {
        if (contact.account) {
          return (
            <Chip
              key={contact.email}
              avatar={<User>{getInitials(contact.account.name)}</User>}
              label={contact.account.name}
            />
          )
        }
        return <Chip label={contact.email} key={contact.email} />
      }
      return null
    })
    to = toContactList.slice(0, 3)
    additionalToCount = toContactList.length > 3 ? toContactList.length - 3 : 0
    subject = frontMessageGroup?.subject ?? ''
  }
  const actions = (
    <Fragment>
      <PopoverListItem button onClick={showModal}>
        <PopoverListItemIcon>
          <PencilAltIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Edit</PopoverListItemText>
      </PopoverListItem>
      <PopoverListItem button onClick={() => showDeleteModal()}>
        <PopoverListItemIcon>
          <TrashIcon />
        </PopoverListItemIcon>
        <PopoverListItemText>Delete</PopoverListItemText>
      </PopoverListItem>
    </Fragment>
  )
  const infoCardProps = {
    logo: (
      <GreyIconWrapper>{getInteractionIcon(interaction.type, frontMessageGroup?.is_inbound ?? false)}</GreyIconWrapper>
    ),
    leftTitle: recordedBy,
    leftSubtitle: createdAt,
    action: interaction.type !== 'email' && (
      <PopoverAction icon={<EllipsisVIcon />} actionMenu={!isPreview && actions} />
    ),
    notes: !isPreview && (
      <Fragment>
        {frontMessageGroup && (
          <Fragment>
            <Box mb={1} className={classes.boxClass}>
              <div>
                <Typography variant="caption">from: </Typography>
                <span>{from}</span>
              </div>
              <div>
                <Typography variant="caption">to/cc: </Typography>
                <span>
                  {to}
                  {!!additionalToCount && ` And ${additionalToCount} more`}
                </span>
              </div>
            </Box>
            <Typography variant="caption">Subject: {subject}</Typography>
          </Fragment>
        )}
        {frontMessageGroup ? (
          <div style={{ maxHeight: 80, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Typography variant="body2" display="block" color="textPrimary">
              {frontMessageGroup?.content}
            </Typography>
          </div>
        ) : (
          <Typography variant="body2" display="block" color="textPrimary">
            <CommunicationInput value={value} notes={interaction.notes} label="Details" readOnly />
          </Typography>
        )}
        {frontMessageGroup && (
          <Box mt={2}>
            <Link
              href={`https://app.frontapp.com/open/${frontMessageGroup?.front_message?.front_msg_id}/`}
              target="_blank">
              <Button fullWidth>View more</Button>
            </Link>
          </Box>
        )}
        {(!isSubjectOfInteraction ||
          interaction.rolled_up_to_parent_accounts.length > 0 ||
          interaction.rolled_up_from_child_accounts.length > 0) && (
          <Fragment>
            <Box mt={1.5} mb={1.5} mr={-4} ml={-4}>
              <Divider light />
            </Box>
            <Typography variant="body2" display="block" color="primary">
              <b>
                <CommunicationSource interaction={interaction} />
              </b>
            </Typography>
          </Fragment>
        )}
      </Fragment>
    ),
  }
  return <SingleInfoCard {...infoCardProps} />
}
